import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Star, ChevronUp, ChevronDown } from 'lucide-react';

// Featured Google reviews for action bar
const FEATURED_REVIEWS = [
  {
    name: 'Mehmet Kaya',
    rating: 5,
    text_tr: 'Küre Dağları\'nın tam kalbinde inanılmaz bir konaklama deneyimi!',
    text_en: 'Amazing accommodation experience in the heart of Küre Mountains!',
  },
  {
    name: 'Sarah Mitchell',
    rating: 5,
    text_tr: 'Doğa ile iç içe mükemmel bir kaçış, kesinlikle tekrar geleceğiz.',
    text_en: 'Perfect escape surrounded by nature, we will definitely come back.',
  },
  {
    name: 'Fatma Öztürk',
    rating: 5,
    text_tr: 'Balayımız için biçilmiş kaftan, muhteşem hizmet ve ortam!',
    text_en: 'Perfect for our honeymoon, amazing service and atmosphere!',
  },
  {
    name: 'Klaus Werner',
    rating: 5,
    text_tr: 'Taş bina odaları tarihi charm ile modern konforu birleştiriyor.',
    text_en: 'Stone building rooms blend historic charm with modern comfort.',
  },
  {
    name: 'Ayşe Demir',
    rating: 5,
    text_tr: 'Hayatımda gittiğim en güzel otellerden biri, herkese tavsiye ederim!',
    text_en: 'One of the most beautiful hotels I have visited, highly recommend!',
  },
];

export default function ActionBar() {
  const { language } = useApp();
  const [currentReview, setCurrentReview] = useState(0);
  const [isOpen, setIsOpen] = useState(() => {
    // Check localStorage
    const stored = localStorage.getItem('actionBarOpen');
    return stored === null ? true : stored === 'true';
  });
  const [isAnimating, setIsAnimating] = useState(false);

  // Save open/closed state to localStorage
  useEffect(() => {
    localStorage.setItem('actionBarOpen', String(isOpen));
    // Dispatch custom event for Navbar to listen
    window.dispatchEvent(new CustomEvent('actionBarToggle', { detail: { isOpen } }));
  }, [isOpen]);

  // Auto-rotate reviews every 6 seconds (only when open)
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentReview((prev) => (prev + 1) % FEATURED_REVIEWS.length);
        setIsAnimating(false);
      }, 300);
    }, 6000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const review = FEATURED_REVIEWS[currentReview];
  const reviewText = language === 'tr' ? review.text_tr : review.text_en;

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 110,
        background: 'linear-gradient(135deg, #1F3A2E 0%, #2D5A45 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
      }}
    >
      {/* Main Content - Only visible when open */}
      <div
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: isOpen ? '12px 1rem 8px' : '0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          maxHeight: isOpen ? '200px' : '0px',
          opacity: isOpen ? (isAnimating ? 0.4 : 1) : 0,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {/* Stars */}
        <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={14}
              style={{ color: '#F59E0B', fill: '#F59E0B' }}
            />
          ))}
        </div>

        {/* Review Text */}
        <p
          style={{
            color: 'white',
            fontSize: '13px',
            fontWeight: 500,
            margin: 0,
            textAlign: 'center',
            fontFamily: 'Manrope, sans-serif',
            lineHeight: 1.5,
            maxWidth: '90%',
            padding: '0 8px',
          }}
          className="review-text"
        >
          <span style={{ opacity: 0.7, marginRight: '4px', display: 'none' }} className="show-desktop">
            {review.name}:
          </span>
          "{reviewText}"
        </p>
      </div>

      {/* Toggle Button Bar - Always visible */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 1rem',
          borderTop: isOpen ? '1px solid rgba(255,255,255,0.1)' : 'none',
        }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Minimize reviews' : 'Show reviews'}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '20px',
            color: 'white',
            cursor: 'pointer',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            fontSize: '12px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 600,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
          }}
        >
          {isOpen ? (
            <>
              <ChevronUp size={16} />
              <span>{language === 'tr' ? 'Gizle' : 'Hide'}</span>
            </>
          ) : (
            <>
              <ChevronDown size={16} />
              <span>
                {language === 'tr' ? 'Müşteri Yorumları' : 'Reviews'}
              </span>
            </>
          )}
        </button>
      </div>

      {/* Styles */}
      <style>{`
        @media (min-width: 640px) {
          .show-desktop {
            display: inline !important;
          }
          .review-text {
            max-width: 800px !important;
          }
        }
        @media (max-width: 639px) {
          .review-text {
            font-size: 12px !important;
            line-height: 1.6 !important;
          }
        }
      `}</style>
    </div>
  );
}


